import { ChangeDetectorRef, Component, ComponentRef, Inject, OnDestroy, OnInit } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { AlertType, DzToastAlert } from '@blk/daisy/combinations/notifications';
import { V1ExternalAccountInfo } from '@blk/portfolio-security-server/accountInfo';
import { GlobalAnalyticsMapperUtil, TealiumAnalyticsEvent, TealiumAnalyticsService, TealiumConstants, TrackEventUtil } from '@ddt-web/analytics';
import { APP_STORE_CONFIG_TOKEN, ApplicationPathConstants, ErrorDCR } from '@ddt-web/config';
import { AppStoreConfig, DataUserDataService, PasDataServiceApiConstants, PasRcResolverService, UpdatePreferenceResponse } from '@ddt-web/data-service';
import { DialogConfig, DialogService } from '@ddt-web/dialog';
import { SharedApplicationConstants, UserDataJsonResponse } from '@ddt-web/domain';
import { AssetResolveResponse } from '@ddt-web/pas';
import { AccountErrors, ApplicationStateStoreProxyService, PartnerAccount, PartnerAcknowledgement, PartnerAuthentication, PartnerAuthenticationInfo, PartnerPlatform, PerformanceState } from '@ddt-web/shared-stores';
import { StoreService } from '@ddt-web/store-service';
import { UrlRedirectService, UrlService } from '@ddt-web/url';
import { PartnerApiError, PartnerConstants } from '@ddt-web/user-data';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as cloneDeep from 'lodash/cloneDeep';
import * as get from 'lodash/get';
import * as has from 'lodash/has';
import * as partition from 'lodash/partition';
import * as set from 'lodash/set';
import { Subscription } from 'rxjs';
import { first, take } from 'rxjs/operators';

import { NotificationType } from '../constants/partner-platform.constants';
import { PartnerAcknowledgementService, PartnerNotificationService, PartnerPortfolioImportService, PartnerPortfoliosDataService, PartnerPortfoliosLoginService, PartnerPortfolioValidatorService } from '../services';

@UntilDestroy()
@Component({
	selector: 'ipp-partner-portfolios',
	templateUrl: './partner-portfolios-dialog.component.html',
	styleUrls: ['./partner-portfolios-dialog.component.scss']
})
export class PartnerPortfoliosDialogComponent implements OnInit, OnDestroy {

	public dialogWidth: string | number = '848px';
	public stepIndex: number = 0;
	public selectedPartner: PartnerPlatform = null;
	public toastNotification: DzToastAlert[] = [];
	public isAuthInProgress: boolean = false;
	public isImporting = false;
	public isFetchingData = false;
	public mode: string = '';
	public selectedPortfolios: Map<string, V1ExternalAccountInfo>;
	public componentRef: ComponentRef<any>;
	public hasInvalidPortfolios: boolean = false;
	public portfolioNamesHaveErrors: boolean = false;
	public missingPortfolioDataIds: string[] = [];
	public wizardStepLabels: Array<string> = [];
	public showAcknowledgement: boolean = false;
	public isSavingAcknowledgement: boolean = false;
	public partnerAcknowledgementInfo: PartnerAcknowledgement = {};

	private validatePortfoliosSubscription: Subscription;
	private accountErrorMap: Map<string, AccountErrors>;
	private rc: string;
	private resolvedAssets: AssetResolveResponse;
	private accounts: PartnerAccount[] = [];
	private partnerAuthentication: PartnerAuthentication = {};
	private keepStateOnClose: boolean = false;

	constructor(
		private config: DialogConfig,
		private partnerPortfoliosLoginService: PartnerPortfoliosLoginService,
		private dialogService: DialogService,
		private urlRedirectService: UrlRedirectService,
		private applicationStateStoreProxyService: ApplicationStateStoreProxyService,
		private pasRcResolverService: PasRcResolverService,
		private dataUserDataService: DataUserDataService,
		private partnerPortfolioImportService: PartnerPortfolioImportService,
		private partnerPortfolioValidatorService: PartnerPortfolioValidatorService,
		private partnerPortfoliosDataService: PartnerPortfoliosDataService,
		private urlService: UrlService,
		private partnerNotificationService: PartnerNotificationService,
		private tealiumAnalyticsService: TealiumAnalyticsService,
		private storeService: StoreService,
		private ref: ChangeDetectorRef,
		private partnerAcknowledgementService: PartnerAcknowledgementService,
		private contentService: ContentService,
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig
	) {
		this.mode = get(config.data, 'mode', this.mode);

		if (!has(config, 'style.width')) {
			// Set default width
			set(config, 'style.width', this.dialogWidth);
		}
	}

	public ngOnInit() {
		this.initRc();
		this.initSelectedPartnerAndSelectedPortfolios();
		this.initStartIndex();
		this.initPartnerAuthentication();
		this.initPartnerAcknowledgement();
		this.wizardStepLabels = [
			this.contentService.get('PARTNER_PORTFOLIOS_STEP1_TITLE', 'Select Platform'),
			this.contentService.get('PARTNER_PORTFOLIOS_STEP2_TITLE', 'Select Portfolios'),
			this.contentService.get('PARTNER_PORTFOLIOS_STEP3_TITLE', 'Edit Details')
		];
	}

	public ngOnDestroy(): void {
		if (!this.keepStateOnClose) {
			GlobalAnalyticsMapperUtil.setImportPartnerName();
			this.applicationStateStoreProxyService.resetImportPartnerPortfoliosState();
		}
		if (this.validatePortfoliosSubscription) {
			this.validatePortfoliosSubscription.unsubscribe();
		}
	}

	public onStepIndexChange(stepIndex: number): void {
		const hasStepIndexChanged = stepIndex !== this.stepIndex;
		if (hasStepIndexChanged) {
			this.toastNotification = [];
			const isUserAcknowledgementRequired = this.partnerAcknowledgementInfo[this.selectedPartner?.id]?.isUserAcknowledgementRequired;

			if (this.stepIndex === 0 && isUserAcknowledgementRequired) {
				if (!this.showAcknowledgement) {
					this.showAcknowledgement = true;
				} else {
					this.storeUserAcknowledgement();
				}
				return;
			}

			this.stepIndex = stepIndex;
			if (stepIndex === 1 && !this.partnerAuthentication[this.selectedPartner.id]?.isAuthenticated && !this.isAuthInProgress) {
				this.initiatePartnerLogin();
				this.applicationStateStoreProxyService.updateImportPartnerPortfoliosSelectedPortfolios(new Map());
			}

			if (stepIndex === 2) {
				this.handleFetchAccountAndAssetData();
			}
		}
		this.dialogService.dialogComponentRef.instance.closeAnalyticsEvent = `import-external-portfolio-popup:close:step${(stepIndex + 1)}`;
	}

	public onImportPortfolios(): void {
		this.isImporting = true;
		this.toastNotification = [];
		const urlIds = this.mode === SharedApplicationConstants.DIALOG_TABLE_MODE.COMPARE ? this.urlService.getPortfolioIds() : [];

		this.validatePortfoliosSubscription = this.partnerPortfolioImportService.validatePortfolioNamesAndSavePartnerPortfolios(
			this.selectedPortfolios,
			this.accountErrorMap,
			this.accounts,
			this.resolvedAssets,
			this.selectedPartner,
			urlIds,
			this.mode === SharedApplicationConstants.DIALOG_TABLE_MODE.COMPARE
		).pipe(first())
			.subscribe({
				next: (responses: UserDataJsonResponse[]) => {
					if (responses?.some(response => !response.success)) {
						this.handleUnsuccessfulPortfolioSave(new Error(ErrorDCR.PARTNER_EXTERNAL_PORTFOLIO_SAVE_API_ERROR));
						return;
					}
					this.handleClose(!responses);
					this.isImporting = false;
					if (responses) {
						this.handleSuccessfulPortfolioSave(responses);
					}
				},
				error: (error) => {
					this.isImporting = false;
					this.handleUnsuccessfulPortfolioSave(error);
				}

			});
	}

	public handlePortfolioEditNotifications(event: DzToastAlert): void {
		if (!this.toastNotification.length || !this.toastNotification.some(toast => toast.type === AlertType.error)) {
			this.toastNotification = [event];
		}
	}

	public handleSearchError(error: PartnerApiError): void {
		if (error?.status === 401 || error?.error === PartnerConstants.PARTNER.UNAUTHORIZED_ERROR_KEY) {
			this.toastNotification = this.partnerNotificationService.showNotification(NotificationType.UNAUTHORIZED_ERROR);
			this.applicationStateStoreProxyService.updatePartnerAuthenticationInfo(this.selectedPartner.id, null);
			this.initiatePartnerLogin();
		} else if (error) {
			this.toastNotification = this.partnerNotificationService.showNotification(NotificationType.API_ERROR);
		}
	}

	public onIsFormValid(isFormValid: 'VALID' | 'INVALID') {
		this.portfolioNamesHaveErrors = isFormValid !== 'VALID';
	}

	public onPartnerAcknowledgementBack(): void {
		this.showAcknowledgement = false;
	}

	private handleSuccessfulPortfolioSave(responses: UserDataJsonResponse[]) {
		const isCompareMode = this.mode === SharedApplicationConstants.DIALOG_TABLE_MODE.COMPARE;
		const portfoliosToSave = responses.map((response: UserDataJsonResponse) => response.success && response.resultMap?.SEARCH_EDIT_RESULTS[0]?.resultList[0]);
		const partnerIds = portfoliosToSave.map(portfolio => PartnerConstants.PARTNER.EXT_IDENTIFIER + portfolio.uniqueId);
		const urlIds = isCompareMode ? this.urlService.getPortfolioIds() : [];
		const redirectIds = Array.from(new Set([...urlIds, ...partnerIds]));

		this.triggerImportTrackingEvents(partnerIds, redirectIds);

		this.dataUserDataService.updateStoreFromResponse(['associatedPortfolios', 'userPortfolios'], portfoliosToSave, partnerIds);

		if (!isCompareMode) {
			this.applicationStateStoreProxyService.updateCustomAdvisoryFee({ customAdvisoryFee: null } as PerformanceState);
		}
		this.urlRedirectService.redirectToTabByQueryParam(
			redirectIds,
			ApplicationPathConstants.PORTFOLIO_ANALYSIS_PATHS,
			isCompareMode,
			{ importPartnerPortfolioMode: 'save' }
		);
	}

	private handleUnsuccessfulPortfolioSave(error: PartnerApiError) {
		this.isImporting = false;
		this.portfolioNamesHaveErrors = false;
		if (error.error === PartnerConstants.PARTNER.UNAUTHORIZED_ERROR_KEY) {
			console.error('An API error occurred with saving one or more portfolios', error);
			this.toastNotification = this.partnerNotificationService.showNotification(NotificationType.UNAUTHORIZED_ERROR);
		} else if (error.error === PartnerConstants.PARTNER.DUPLICATE_PORTFOLIO_NAME_ERROR) {
			console.error('Some portfolios have names that already exist', error);
		} else {
			console.error('An error occurred with saving one or more portfolios', error);
			this.toastNotification = this.partnerNotificationService.showNotification(NotificationType.API_ERROR);
		}
	}

	private initiatePartnerLogin(): void {
		this.isAuthInProgress = true;
		this.partnerPortfoliosLoginService.checkAuthentication(this.selectedPartner)
			.pipe(first())
			.subscribe(partnerAuthenticationInfo => {
				this.partnerAuthentication[this.selectedPartner.id] = partnerAuthenticationInfo;
				const isAuthFromLocalStorage = !!partnerAuthenticationInfo?.timeout;
				this.handlePartnerAuthenticationResponse(partnerAuthenticationInfo, !isAuthFromLocalStorage);
			});
	}

	private handlePartnerAuthenticationResponse(partnerAuthenticationInfo: PartnerAuthenticationInfo, showNotification: boolean = true): void {
		this.isAuthInProgress = false;
		let notificationType = partnerAuthenticationInfo.isAuthenticated ? NotificationType.LOGIN_SUCCESS : NotificationType.LOGIN_ERROR;
		if (partnerAuthenticationInfo.apiErrors) {
			notificationType = NotificationType.API_ERROR;
		}
		if (showNotification) {
			this.toastNotification = this.partnerNotificationService.showNotification(notificationType, this.selectedPartner.id);
		}
		if (!partnerAuthenticationInfo.isAuthenticated) {
			this.stepIndex = 0;
		}
	}

	private initRc(): void {
		this.pasRcResolverService.getRcForUserType(PasDataServiceApiConstants.SECURITY_DETAILS).pipe(untilDestroyed(this)).subscribe(rc => this.rc = rc);
	}

	private handleFetchAccountAndAssetData(): void {
		this.isFetchingData = true;
		const selectedPortfoliosIds: string[] = Array.from(this.selectedPortfolios.keys());
		this.partnerPortfolioImportService.fetchAccountAndAssetData(
			selectedPortfoliosIds, this.selectedPartner.id, this.rc
		).pipe(
			take(1)
		).subscribe({
			next: ([resolvedAssets, accounts]) => {
				GlobalAnalyticsMapperUtil.setImportPartnerName(this.selectedPartner.id);
				if (!accounts.length) {
					// go back to step 2 and show error notification if empty response is received
					this.handleUnsuccessfulAccountDataFetch({ error: 'Invalid Portfolios' });
				} else if (accounts.length !== selectedPortfoliosIds.length) {
					// handle partial response and remove securities from analysis for which data is not received
					this.handlePartialPortfolioResponse(accounts, resolvedAssets, selectedPortfoliosIds);
				} else {
					this.handleSuccessfulAccountDataFetch(resolvedAssets, accounts);
				}
			},
			error: (error: PartnerApiError) => {
				this.handleUnsuccessfulAccountDataFetch(error);
			}
		});
	}

	private handleUnsuccessfulAccountDataFetch(error: PartnerApiError): void {
		this.isFetchingData = false;
		// Redirect user to account search
		this.stepIndex = 1;
		if (error.error === PartnerConstants.PARTNER.UNAUTHORIZED_ERROR_KEY) {
			console.error('An unauthorized error occurred with fetching accounts or assets', error);
			this.toastNotification = this.partnerNotificationService.showNotification(NotificationType.UNAUTHORIZED_ERROR);
		} else {
			console.error('An error occurred with fetching accounts or assets', error);
			this.toastNotification = this.partnerNotificationService.showNotification(NotificationType.API_ERROR);
		}
		this.ref.detectChanges();
	}

	private handleSuccessfulAccountDataFetch(resolvedAssets, accounts): void {
		this.applicationStateStoreProxyService.updateImportPartnerPortfoliosResolvedAssets(resolvedAssets.assets);
		this.applicationStateStoreProxyService.updateImportPartnerPortfoliosPortfolioData(accounts);
		this.accountErrorMap = this.partnerPortfolioValidatorService.validatePortfolios(accounts, resolvedAssets);
		this.resolvedAssets = resolvedAssets;
		this.accounts = accounts;
		this.isFetchingData = false;
	}

	private initSelectedPartnerAndSelectedPortfolios(): void {
		this.applicationStateStoreProxyService.getImportPartnerPortfoliosState()
			.pipe(untilDestroyed(this))
			.subscribe(state => {
				this.selectedPartner = state.partnerPlatform;
				this.selectedPortfolios = state.selectedPortfolios;
				this.accountErrorMap = state.accountErrorMap;
				this.accounts = state.portfolioData;
				this.resolvedAssets = { assets: state.resolvedAssets };

				if (this.selectedPartner?.id) {
					GlobalAnalyticsMapperUtil.setImportPartnerName(this.selectedPartner.id);
				}

				this.initInvalidPortfolios();

				if (!this.selectedPortfolios.size && this.stepIndex === 2) {
					this.stepIndex = 1;
				}
			});
	}

	private initInvalidPortfolios(): void {
		this.hasInvalidPortfolios = Array.from(this.accountErrorMap.values()).some(errorInfo =>
			errorInfo.hasExactlyOneHolding ||
			errorInfo.hasZeroHoldings ||
			errorInfo.exceedsMaxHoldingPercentage ||
			errorInfo.hasDuplicateMorningstarFundIds ||
			errorInfo.hasTooMuchCash
		);
	}

	private initStartIndex(): void {
		this.stepIndex = this.config.data?.openAtIndex || 0;
		if (this.mode === SharedApplicationConstants.DIALOG_TABLE_MODE.COMPARE) {
			this.partnerPortfoliosDataService.retrieveAndFilterPartnerList(true).pipe(take(1)).subscribe(partnerList => {
				const selectedPartner = partnerList.find(partner => !!partner.isSelected);
				if (selectedPartner) {
					this.applicationStateStoreProxyService.updateImportPartnerPortfoliosPartnerPlatform(selectedPartner);
					this.stepIndex = 1;
				}
			});
		}
	}

	private initPartnerAuthentication(): void {
		this.applicationStateStoreProxyService.getPartnerAuthenticationState()
			.pipe(untilDestroyed(this))
			.subscribe(partnerAuthentication => this.partnerAuthentication = cloneDeep(partnerAuthentication));
	}

	private handleClose(keepStateOnClose: boolean) {
		this.keepStateOnClose = keepStateOnClose;
		this.dialogService.close();
	}

	private triggerImportTrackingEvents(partnerIds: string[], redirectIds: string[]): void {
		// Track portfolios that have been refreshed. If the imported portfolio is already present in the store then it is a refreshed portfolio
		this.storeService.selectAll(this.storeConfig['userPortfolios']).pipe(
			take(1)
		).subscribe(userPortfolioState => {
			const [refreshedPortfolioIds, newPortfolioIds] = partition(partnerIds, partnerId => Object.values(userPortfolioState.ids).includes(partnerId));
			if (refreshedPortfolioIds.length) {
				this.tealiumAnalyticsService.trackEventTealium(
					new TealiumAnalyticsEvent(TealiumConstants.IMPORT_PARTNER_PORTFOLIO_EVENTS.PORTFOLIO_REFRESH, { portfolioIds: refreshedPortfolioIds.join() })
				);
			}
			if (newPortfolioIds.length) {
				this.tealiumAnalyticsService.trackEventTealium(
					new TealiumAnalyticsEvent(TealiumConstants.IMPORT_PARTNER_PORTFOLIO_EVENTS.NEW_PARTNER_PORTFOLIO_IMPORT, { portfolioIds: newPortfolioIds.join() })
				);
			}
		});

		// track tool completion event
		this.tealiumAnalyticsService.triggerToolCompletion(redirectIds.join(), null, TealiumConstants.LINK_NAMES.IMPORT_PARTNER_PORTFOLIO_EVENT);

		// trigger add to compare event with portfolios added for analysis
		if (this.mode === SharedApplicationConstants.DIALOG_TABLE_MODE.COMPARE) {
			const event = TrackEventUtil.createPortfolioChangeEvent(
				TealiumConstants.LINK_NAMES.ADD_TO_COMPARE_EVENT,
				redirectIds.join(),
				TealiumConstants.EVENT_PROPERTY_NAMES.PORTFOLIO_ADDED,
				partnerIds.join()
			);
			this.tealiumAnalyticsService.trackEventTealium(event);
		}
	}

	private handlePartialPortfolioResponse(accounts: any, resolvedAssets: any, selectedPortfoliosIds: string[]): void {
		const successfulPortfolioDataIds = accounts.map(account => account.id);
		this.missingPortfolioDataIds = selectedPortfoliosIds.filter(id => !successfulPortfolioDataIds.includes(id));
		this.missingPortfolioDataIds?.forEach(portfolioId => this.selectedPortfolios.delete(portfolioId));
		this.applicationStateStoreProxyService.updateImportPartnerPortfoliosSelectedPortfolios(this.selectedPortfolios);
		this.handleSuccessfulAccountDataFetch(resolvedAssets, accounts);
	}

	private initPartnerAcknowledgement(): void {
		this.partnerAcknowledgementService.retrievePartnerAcknowledgementInfo()
			.pipe(take(1))
			.subscribe(partnerAcknowledgementInfo => {
				this.partnerAcknowledgementInfo = cloneDeep(partnerAcknowledgementInfo);
			});
	}

	private storeUserAcknowledgement(): void {
		this.isSavingAcknowledgement = true;
		const currentAcknowledgementHash = this.partnerAcknowledgementInfo[this.selectedPartner.id].currentAcknowledgementMsgHash;
		this.partnerAcknowledgementService.storeUserAcknowledgement(
			currentAcknowledgementHash,
			this.selectedPartner?.id
		)
			.pipe(take(1))
			.subscribe({
				next: (response: UpdatePreferenceResponse) => {
					console.log(response);
					if (!response.success) {
						this.handleUnsuccessfulStoreAcknowledgement(new Error(ErrorDCR.SOMETHING_WRONG_ERROR_TITLE));
					} else {
						this.handleSuccessfulStoreUserAcknowledgement(currentAcknowledgementHash);
					}
				},
				error: (error) => {
					this.handleUnsuccessfulStoreAcknowledgement(error);
				}
			});
	}

	private handleSuccessfulStoreUserAcknowledgement(currentAcknowledgementHash: string): void {
		this.isSavingAcknowledgement = false;
		this.showAcknowledgement = false;
		const acknowledgementDetails = {
			currentAcknowledgementMsgHash: currentAcknowledgementHash,
			lastUserAcknowledgedMsgHash: currentAcknowledgementHash,
			isUserAcknowledgementRequired: false
		};
		this.partnerAcknowledgementInfo[this.selectedPartner.id] = acknowledgementDetails;
		this.applicationStateStoreProxyService.updatePartnerAcknowledgementState({
			[this.selectedPartner.id]: acknowledgementDetails
		});
		this.stepIndex = 1;
		if (!this.partnerAuthentication[this.selectedPartner.id]?.isAuthenticated && !this.isAuthInProgress) {
			this.initiatePartnerLogin();
			this.applicationStateStoreProxyService.updateImportPartnerPortfoliosSelectedPortfolios(new Map());
		}
	}

	private handleUnsuccessfulStoreAcknowledgement(error): void {
		this.isSavingAcknowledgement = false;
		this.showAcknowledgement = false;
		this.toastNotification = this.partnerNotificationService.showNotification(NotificationType.API_ERROR);
		console.error('An API error occurred while saving acknowledgement', error);
	}
}
